/**
 * Route table for the admin console. Exported as a plain array so tests can mount the REAL table
 * under a memory router rather than a stand-in that could drift from it.
 *
 * Every route that touches PII is wrapped in `RequireStaff`, which returns `<Navigate>` instead
 * of its children when there is no staff token — so the screen never mounts, and its queries
 * never fire. `/login` is the one route outside the guard, and it renders no PII of its own.
 */
import { Navigate, type RouteObject } from 'react-router-dom';
import { RequireStaff } from '@/auth/RequireStaff';
import { Login } from '@/routes/Login';
import { Queue } from '@/routes/Queue';
import { DeadLetters } from '@/routes/DeadLetters';
import { Suppressions } from '@/routes/Suppressions';
import { Lookup } from '@/routes/Lookup';
import { Fraud } from '@/routes/Fraud';
import { Dashboard } from '@/routes/Dashboard';

export const routes: RouteObject[] = [
  { path: '/', element: <Navigate to="/dashboard" replace /> },
  { path: '/login', element: <Login /> },
  {
    path: '/dashboard',
    element: (
      <RequireStaff>
        <Dashboard />
      </RequireStaff>
    ),
  },
  {
    path: '/queue',
    element: (
      <RequireStaff>
        <Queue />
      </RequireStaff>
    ),
  },
  {
    path: '/dead-letters',
    element: (
      <RequireStaff>
        <DeadLetters />
      </RequireStaff>
    ),
  },
  {
    path: '/suppressions',
    element: (
      <RequireStaff>
        <Suppressions />
      </RequireStaff>
    ),
  },
  {
    path: '/lookup',
    element: (
      <RequireStaff>
        <Lookup />
      </RequireStaff>
    ),
  },
  {
    path: '/fraud',
    element: (
      <RequireStaff>
        <Fraud />
      </RequireStaff>
    ),
  },
  // Unknown path → the queue, which is itself guarded. An unknown path is never a reason to
  // render anything.
  { path: '*', element: <Navigate to="/dashboard" replace /> },
];
